// What a published game offers, as markup: the three actions (Play, Download,
// Remix), the download panel with a tile per device, the source and the past
// versions, the rows of the games it was remixed into, and its card on the
// landing page's shelf. One writer serves the game's page (server/listing.ts
// writes it into the document), the landing page (the server writes the
// cards into it and src/gate/shelf.ts writes them where the server did not)
// and the dialogs the landing page and the play page's dock open (src/gate/
// puts the same markup into a dialog), so the places cannot drift apart.
//
// Everything here is a string made from GameFacts or from the showcase's
// games. Nothing imports the DOM, and every value that came from an author
// is escaped.

import { icon, type IconName } from "./icons.ts";
import { formatDay, formatList, keepNumbers, LANG_PARAM, localePath, phraseRuns, translate, type Lang, type MessageKey } from "./i18n.ts";
import { HELP_PATH } from "./install-guide.ts";
import type { GameFacts, PastPackage, RelatedGame, ShowcaseGame } from "./protocol.ts";
import type { NativeTarget } from "./types.ts";

const escape = (text: string): string => text.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

/** A sentence as HTML in `lang`: escaped, each number kept with its counter, each device name in a span that does not break. */
const phrase = (lang: Lang, text: string): string =>
  phraseRuns(lang, text).map((run) => (run.name ? `<span class="nb">${escape(run.text)}</span>` : escape(run.text))).join("");

/** A file's size as a visitor reads it: whole kilobytes under half a megabyte, whole megabytes, or gigabytes with one decimal. */
export function sizeText(bytes: number): string {
  if (bytes < 500_000) return `${Math.max(1, Math.round(bytes / 1000))} KB`;
  const megabytes = bytes / 1_000_000;
  if (megabytes < 1000) return `${Math.max(1, Math.round(megabytes))} MB`;
  return `${(megabytes / 1000).toFixed(1)} GB`;
}

/** The Studio's origin, from a game's page address. */
const studioOf = (facts: GameFacts): string => new URL(facts.page).origin;

/**
 * The address that hands over a device's package: for an account the file
 * itself, for anyone else the game's page with the wish in it (GET
 * /api/get/:slug/:device). `version` names a past version.
 */
export function packageHref(facts: GameFacts, target: NativeTarget, version?: string): string {
  return `${studioOf(facts)}/api/get/${encodeURIComponent(facts.slug)}/${target}${version === undefined ? "" : `?version=${encodeURIComponent(version)}`}`;
}

/** The game's page with something for its script to open: `get`, `remix` or `versions`. `from` says where the visitor came from. */
export function pageWith(facts: GameFacts, open: "get" | "remix" | "versions", from?: string): string {
  const page = new URL(facts.page);
  page.searchParams.set(open, "");
  if (from) page.searchParams.set("from", from);
  // (`?get=` reads as `?get`: the value is nothing)
  return page.toString().replace(/=(?=&|$)/g, "");
}

/** What a game has to hand over: a package, or its source. */
export const hasDownloads = (facts: GameFacts): boolean => facts.packages.length > 0 || facts.source !== null;

/** What the three actions are drawn from: a game's facts, or as much of them as the showcase gives a card. */
export interface Offered {
  slug: string;
  title: string;
  /** The game's own address when it runs the game in a browser. */
  play: string | null;
  page: string;
  /** Whether there is a package or the source to hand over. */
  downloads: boolean;
  remixable: boolean;
}

/** A game's facts as the actions read them. */
export const offered = (facts: GameFacts): Offered => ({ slug: facts.slug, title: facts.title, play: facts.play, page: facts.page, downloads: hasDownloads(facts), remixable: facts.remixable });

export interface ActionsOptions {
  /**
   * Whether the Play button is drawn: not on the play page itself, and not in
   * a browser that cannot run the game (the caller knows; a page written by
   * the server draws it whenever the game has a browser version).
   */
  play?: boolean;
  /** Where the buttons are, for the event a press says: `data-at`. */
  at?: string;
  /** The game's id, for that event: `data-app`. */
  app?: string;
}

/**
 * The three actions as links, each with its picture and one word. Each works
 * with no script: Play goes to the game, Download to the download part of
 * the game's page, Remix to the page with the remix dialog asked for. A
 * page's script takes the last two over (`data-act`).
 */
export function actionsHtml(game: Offered, lang: Lang, options: ActionsOptions = {}): string {
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params);
  const said = `${options.at ? ` data-at="${escape(options.at)}"` : ""}${options.app ? ` data-app="${escape(options.app)}"` : ""}`;
  const withOpen = (open: "remix") => {
    const page = new URL(game.page);
    page.searchParams.set(open, "");
    return page.toString().replace(/=(?=&|$)/g, "");
  };
  const parts: string[] = [];
  if (game.play && options.play !== false) {
    const play = new URL(game.play);
    if (lang !== "en") play.searchParams.set(LANG_PARAM, lang);
    parts.push(`<a class="act play" data-act="play" data-to="play"${said} href="${escape(play.toString())}" aria-label="${escape(t("act.play.of", { title: game.title }))}">${icon("play")}<span>${escape(t("act.play"))}</span></a>`);
  }
  if (game.downloads) {
    parts.push(`<a class="act" data-act="download" data-to="download"${said} href="${escape(`${game.page}#download`)}" aria-label="${escape(t("act.download.of", { title: game.title }))}">${icon("download")}<span>${escape(t("act.download"))}</span></a>`);
  }
  if (game.remixable) {
    parts.push(`<a class="act" data-act="remix" data-to="remix"${said} href="${escape(withOpen("remix"))}" aria-label="${escape(t("act.remix.of", { title: game.title }))}">${icon("remix")}<span>${escape(t("act.remix"))}</span></a>`);
  }
  return parts.length > 0 ? `<p class="acts3" data-slug="${escape(game.slug)}">${parts.join("")}</p>` : "";
}

/** A device as a chip names it: its short name, the same in every language. A chip's `title` is the full name. */
const CHIP_NAMES: Record<string, string> = { psp: "PSP", vita: "PS Vita", "3ds": "3DS", "ipod-touch": "iPod touch", android: "Android" };
/** The ids a chip has a picture for: the devices, and a desktop computer. */
const CHIP_ICONS: readonly string[] = ["psp", "vita", "3ds", "ipod-touch", "android", "desktop"];

/**
 * The devices something runs on, as a row of chips: a small picture of each
 * device and its short name. `name` is the device's full name in `lang`, for
 * the chip's `title`; `desktop` is a desktop computer. The landing page's
 * cards have it under their words: a game's by its packages (caseHtml), a
 * mod's by what its page names (`data-devices`, scripts/pages.ts). `tag` is
 * `span` inside a button, which takes no list. Empty for no device.
 */
export function devicesHtml(devices: ReadonlyArray<{ id: string; name?: string }>, lang: Lang, tag: "ul" | "span" = "ul"): string {
  if (devices.length === 0) return "";
  const item = tag === "ul" ? "li" : "span";
  const chips = devices.map(({ id, name }) => {
    const label = id === "desktop" ? translate(lang, "shelf.desktop") : (CHIP_NAMES[id] ?? name ?? id);
    const full = name && name !== label ? ` title="${escape(name)}"` : "";
    return `<${item} class="dev"${tag === "ul" ? "" : ' role="listitem"'}${full}>${CHIP_ICONS.includes(id) ? icon(id as IconName, 16) : ""}${escape(label)}</${item}>`;
  });
  return `<${tag} class="devs"${tag === "ul" ? "" : ' role="list"'} aria-label="${escape(translate(lang, "shelf.on"))}">${chips.join("")}</${tag}>`;
}

/**
 * One game of the showcase as a card of the landing page's shelf and of the
 * index /games/: its lead clip or picture, a tag, its title and its line,
 * the devices it has packages for as chips, and its three actions. The
 * picture and the title lead where Play does: to the game's browser
 * version, or to the game's page where it has none or `plays` is false (a
 * browser with no WebGPU, which the page's script knows and the server does
 * not). The tag says a native game. `from` is the page the card is on, which
 * its page link says: the landing page, or the index, whose links are `at`
 * the index.
 */
export function caseHtml(game: ShowcaseGame, lang: Lang, plays = true, from: "landing" | "games" = "landing"): string {
  const page = new URL(game.page);
  page.searchParams.set("from", from);
  const at = from === "landing" ? "shelf" : "index";
  const play = game.browser && plays ? game.url : null;
  const lead = game.lead;
  // A clip waits behind its poster until the page's script plays it on screen.
  const picture = lead.kind === "video"
    ? `<video muted loop playsinline disableremoteplayback preload="none"${lead.poster ? ` poster="${escape(lead.poster)}"` : ""} width="${lead.width}" height="${lead.height}" aria-label="${escape(lead.caption)}" src="${escape(lead.url)}"></video>`
    : `<img loading="lazy" alt="${escape(lead.caption)}" width="${lead.width}" height="${lead.height}" src="${escape(lead.url)}">`;
  const devices = devicesHtml(game.packages.map((stored) => ({ id: stored.target, name: stored.name })), lang);
  const acts = actionsHtml({ slug: game.slug, title: game.title, play, page: page.href, downloads: game.downloads, remixable: game.remixable }, lang, { at, app: game.id });
  const tag = game.packages.length > 0 ? translate(lang, "shelf.native") : null;
  return (
    `<li class="case" data-slug="${escape(game.slug)}">` +
    `<a href="${escape(play ?? page.href)}" data-to="${play ? "play" : "page"}" data-app="${escape(game.id)}" data-at="${at}">` +
    `<span class="case-pic">${picture}</span><span class="plank" aria-hidden="true"></span>${tag ? `<span class="tag">${escape(tag)}</span>` : ""}<h3>${escape(game.title)}</h3>${game.tagline ? `<p>${phrase(lang, game.tagline)}</p>` : ""}</a>` +
    `${devices}${acts}</li>`
  );
}

/** One tile: what it hands over, a line of facts under it, and the picture of what a press does. */
function tile(attributes: string, name: string, meta: string, picture: string): string {
  return `<li><a class="tile" ${attributes}><span class="tile-name">${name}</span>${meta ? `<span class="tile-meta">${meta}</span>` : ""}<span class="tile-go">${picture}</span></a></li>`;
}

/** A package's line of facts: its version and its size, where each is known. */
function packageMeta(lang: Lang, stored: { version: string; size: number }): string {
  const parts = [stored.version ? translate(lang, "get.version", { version: stored.version }) : "", stored.size > 0 ? sizeText(stored.size) : ""].filter(Boolean);
  return escape(keepNumbers(lang, parts.join(" · ")));
}

/**
 * The game's source as tiles, under their own heading: a group of its own,
 * each tile the weight of a device's. The download panel has it, and so has
 * the remix dialog: the source is a thing to download and what a remix
 * starts from. Empty for a game with no source to hand over.
 */
export function sourceHtml(facts: GameFacts, lang: Lang): string {
  if (!facts.source) return "";
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params);
  const tiles: string[] = [];
  if (facts.source.kind === "repository") {
    const { url, host, archive } = facts.source;
    if (archive) tiles.push(tile(`data-source href="${escape(archive)}" rel="noopener"`, escape(t("get.source")), escape(t("get.source.zip", { host })), icon("download")));
    tiles.push(tile(`data-source href="${escape(url)}" target="_blank" rel="noopener"`, escape(archive ? host : t("get.source")), escape(t("get.source.repo", { host })), icon("out")));
  } else {
    tiles.push(tile(`data-get="source" href="${escape(facts.source.url)}" rel="nofollow"`, escape(t("get.source")), escape(`${t("get.source.archive")} · ${sizeText(facts.source.size)}`), icon("download")));
  }
  return `<h3 class="tiles-group">${escape(t("get.source"))}</h3>
<ul class="tiles source">${tiles.join("")}</ul>
<p class="tiles-say">${escape(t("get.source.note"))}</p>`;
}

export interface DownloadOptions {
  /** The heading's level on a page (2), or none in a dialog whose title says it. */
  heading?: 2 | 3;
  /** Whether the panel says that an unverified game's packages are homebrew. A game's page says it under the game's name instead. */
  homebrew?: boolean;
}

/**
 * The download panel: a tile per device, then the source as tiles of the same
 * weight in a group of their own, then the way to every version, then one
 * line on what a download asks for. A tile is a link that works with no
 * script (`packageHref`); a page's script on the Studio's host takes it over
 * to sign a visitor in where they are (`data-get`).
 */
export function downloadHtml(facts: GameFacts, lang: Lang, options: DownloadOptions = {}): string {
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params);
  const studio = studioOf(facts);
  const out: string[] = [];
  if (options.heading) out.push(`<h${options.heading} class="tiles-title">${escape(t("get.heading"))}</h${options.heading}>`);

  if (facts.packages.length > 0) {
    out.push(`<h3 class="tiles-group">${escape(t("get.devices"))}</h3>`);
    out.push(`<ul class="tiles">${facts.packages
      .map((stored) =>
        tile(
          `data-get="${stored.target}" data-device="${escape(stored.name)}" href="${escape(packageHref(facts, stored.target))}" rel="nofollow" aria-label="${escape(t("game.download", { device: stored.name }))}"`,
          phrase(lang, stored.name),
          packageMeta(lang, stored),
          icon("download"),
        ))
      .join("")}</ul>`);
  } else {
    out.push(`<p class="tiles-none">${escape(t("get.none"))}</p>`);
  }

  out.push(sourceHtml(facts, lang));

  if (facts.packages.length > 0) {
    out.push(`<p class="tiles-versions"><a class="versions" data-act="versions" href="${escape(pageWith(facts, "versions"))}">${icon("versions", 20)}<span>${escape(t("get.versions"))}</span></a><span>${escape(t("get.versions.note"))}</span></p>`);
    const guide = `<a href="${escape(studio + localePath(lang, HELP_PATH))}">${escape(t("get.guide"))}</a>`;
    out.push(`<p class="tiles-note">${escape(t("get.free"))} ${guide}${lang === "ja" ? "" : "."}${facts.homebrew && options.homebrew !== false ? ` ${escape(t("game.homebrew"))}` : ""}</p>`);
  }
  return out.join("\n");
}

/**
 * Every version of the game's packages, by device: the current one first,
 * then the past ones, newest first. A past version's link is a member's to
 * follow; the caller says whether the visitor is one (`member`), and when
 * they are not each past row carries a padlock in place of the arrow.
 */
export function versionsHtml(facts: GameFacts, lang: Lang, member: boolean): string {
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params);
  const past = new Map<NativeTarget, PastPackage[]>();
  for (const entry of facts.past) past.set(entry.target, [...(past.get(entry.target) ?? []), entry]);
  const groups = facts.packages.map((current) => {
    const rows = [
      `<li><a class="version" data-get="${current.target}" data-device="${escape(current.name)}" href="${escape(packageHref(facts, current.target))}" rel="nofollow"><b>${escape(current.version || t("versions.current"))}</b><span>${escape(keepNumbers(lang, [current.version ? t("versions.current") : "", formatDay(lang, current.uploadedAt), current.size > 0 ? sizeText(current.size) : ""].filter(Boolean).join(" · ")))}</span>${icon("download", 20)}</a></li>`,
      ...(past.get(current.target) ?? []).map((entry) => {
        // A past version sent after the current one is a release from before the Studio kept them: the day it was
        // sent says nothing of when it was made, so its row has its size alone.
        const said = [entry.uploadedAt <= current.uploadedAt ? formatDay(lang, entry.uploadedAt) : "", sizeText(entry.size)].filter(Boolean).join(" · ");
        return `<li><a class="version past${member ? "" : " locked"}" data-get="${entry.target}" data-version="${escape(entry.version)}" data-device="${escape(entry.name)}" href="${escape(packageHref(facts, entry.target, entry.version))}" rel="nofollow"><b>${escape(entry.version)}</b><span>${escape(keepNumbers(lang, said))}</span>${icon(member ? "download" : "lock", 20)}</a></li>`;
      }),
    ];
    return `<section class="versions-of"><h3>${phrase(lang, current.name)}</h3><ol>${rows.join("")}</ol></section>`;
  });
  const none = facts.past.length === 0 ? `<p class="versions-none">${escape(t("versions.none"))}</p>` : "";
  return `${groups.join("\n")}${none}`;
}

/** One related game as a card: its picture, its title, who made it, and where it runs. */
function relatedCard(game: RelatedGame, lang: Lang): string {
  const t = (key: MessageKey, params?: Record<string, string | number>) => translate(lang, key, params);
  const where = [game.browser ? t("game.remix.browser") : "", game.devices.length > 0 ? formatList(lang, game.devices) : ""].filter(Boolean).join(" · ");
  const picture = game.picture ? `<img src="${escape(game.picture)}" alt="" loading="lazy">` : "";
  const body = `<span class="rel-pic">${picture}</span><span class="rel-name">${escape(game.title)}</span><span class="rel-by">${escape(t("game.remix.by", { author: game.author }))}</span>${where ? `<span class="rel-on">${phrase(lang, where)}</span>` : ""}`;
  return game.page ? `<li><a class="rel" href="${escape(game.page)}">${body}</a></li>` : `<li><span class="rel">${body}</span></li>`;
}

/**
 * The games started from this one, as cards. A remix is a game of its own:
 * its devices are its own packages, never added to this game's.
 */
export function remixesHtml(facts: GameFacts, lang: Lang): string {
  if (facts.remixes.length === 0) return "";
  const more = facts.remixCount - facts.remixes.length;
  return `<h2>${escape(translate(lang, "game.remixes"))}</h2>
<ul class="rels">${facts.remixes.map((game) => relatedCard(game, lang)).join("")}</ul>${more > 0 ? `\n<p class="rels-more">${escape(translate(lang, "game.remixes.more", { count: more }))}</p>` : ""}`;
}

/** "A remix of <game> by <author>.", the game's name a link to its page when it has one. Empty for a game that is no remix. */
export function remixOfHtml(facts: GameFacts, lang: Lang): string {
  const of = facts.remixOf;
  if (!of) return "";
  const name = of.page ? `<a href="${escape(of.page)}">${escape(of.title)}</a>` : escape(of.title);
  return escape(translate(lang, "game.remixOf", { game: "\u0000", author: of.author })).replace("\u0000", name);
}
